/**
 * Utility functions for managing custom event types per user
 */

import { doc, getDoc, updateDoc, arrayUnion, serverTimestamp, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../app/firebase';

// Standard event types that should not be saved as custom
const STANDARD_EVENT_TYPES = [
  'Vaccination',
  'Anti-Parasitic',
  'Vet Visit',
  'Grooming',
  'Medicine',
  'Other',
];

/**
 * Checks if an event type is custom (not in standard list)
 * @param {string} eventType - The event type to check
 * @returns {boolean}
 */
export const isCustomEventType = (eventType) => {
  if (!eventType || typeof eventType !== 'string') return false;
  return !STANDARD_EVENT_TYPES.includes(eventType.trim());
};

/**
 * Проверяет, существует ли тип события в глобальном каталоге eventTypes (по полю key).
 * @param {string} eventType
 * @returns {Promise<boolean>}
 */
const existsInGlobalEventTypes = async (eventType) => {
  if (!db) return false;
  const trimmed = eventType.trim();
  if (!trimmed) return false;

  try {
    const q = query(
      collection(db, 'eventTypes'),
      where('key', '==', trimmed)
    );
    const snap = await getDocs(q);
    return !snap.empty;
  } catch (error) {
    console.error('Error checking global eventTypes catalog:', error);
    return false;
  }
};

/**
 * Saves a custom event type to user's profile in Firestore
 * @param {string} userId - User ID
 * @param {string} customType - Custom event type name
 * @returns {Promise<void>}
 */
export const saveCustomEventType = async (userId, customType) => {
  if (!userId || !customType) return;
  
  const trimmedType = customType.trim();
  if (!trimmedType || !isCustomEventType(trimmedType)) return;

  // Если такой ключ уже есть в глобальной коллекции eventTypes, не сохраняем как кастомный
  const isGlobal = await existsInGlobalEventTypes(trimmedType);
  if (isGlobal) return;

  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      const existingTypes = userData.customEventTypes || [];

      // Only add if it doesn't already exist
      if (!existingTypes.includes(trimmedType)) {
        await updateDoc(userRef, {
          customEventTypes: arrayUnion(trimmedType),
          updatedAt: serverTimestamp(),
        });
      }
    } else {
      // Create user document if it doesn't exist
      await updateDoc(userRef, {
        customEventTypes: [trimmedType],
        updatedAt: serverTimestamp(),
      }, { merge: true });
    }
  } catch (error) {
    console.error('Error saving custom event type:', error);
    // Don't throw - this is not critical for event creation
  }
};

/**
 * Fetches custom event types from user's profile
 * @param {string} userId - User ID
 * @returns {Promise<string[]>} Array of custom event type names
 */
export const fetchCustomEventTypes = async (userId) => {
  if (!userId) return [];

  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.customEventTypes || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching custom event types:', error);
    return [];
  }
};

